/**
 * "The year ran away from the consensus you were shown" (F16.5 C, R1/R12): the year's miss against the published path in dollars a share, for the plan and for the status-quo twin run on the
 * same realised path, and the net between them. From `Attribution.expectationTerminal` (the plan's surprise leg) and its `twin` legs; null when either is absent or the plan's leg is under the materiality threshold.
 * With `surpriseDrivers` (F19.0) the sentence names what differed: the rates, the card's part of the policy-rate miss, named by its card, unemployment; each clause only when it moved.
 */
import type { RoundReport } from "@bsim/contracts";
import { formatPoints, formatUsdPerShare } from "../format";
import { quotedTitle } from "../format/labels";

const MATERIAL_USD = 0.5;

const AMOUNT_BP = 0.5;
const AMOUNT_PP = 0.05;

/** The miss in words, one clause per driver that moved (policy rate, with the card's share of it; the ten-year yield; unemployment). */
function driversClause(d: NonNullable<NonNullable<RoundReport["attribution"]["expectationTerminal"]>["surpriseDrivers"]>, cards: string | null): string | null {
  const side = (v: number, hi: string, lo: string): string => (v > 0 ? hi : lo);
  const clauses = [
    Math.abs(d.policyRateBp) < AMOUNT_BP ? null : `Rates ended the year ${Math.round(Math.abs(d.policyRateBp))}bp ${side(d.policyRateBp, "above", "below")} the consensus${Math.abs(d.cardPolicyRateBp) < AMOUNT_BP || cards === null ? "" : `, ${Math.round(Math.abs(d.cardPolicyRateBp))}bp of it ${cards}`}`,
    Math.abs(d.tenYearBp) < AMOUNT_BP ? null : `the ten-year yield ended ${Math.round(Math.abs(d.tenYearBp))}bp ${side(d.tenYearBp, "above", "below")} it`,
    Math.abs(d.unemploymentPp) < AMOUNT_PP ? null : `unemployment ${formatPoints(Math.abs(d.unemploymentPp), { signed: false })} ${side(d.unemploymentPp, "higher", "lower")}`,
  ].filter((c) => c !== null);
  return clauses.length === 0 ? null : clauses.join("; ");
}

/**
 * The cards a rate move can be put to (the report carries the move, not which card made it): the cards that ran in the year, round-start and mid-year. `midYearOnly` is true when every one arrived after signing,
 * the only case where the move is certainly a mid-year card's (a round-start card's move sits inside Expected).
 */
export function rateMoveCards(report: RoundReport): { readonly titles: readonly string[]; readonly midYearOnly: boolean } {
  const ran = report.events.filter((e) => e.status !== "cancelled");
  return { titles: ran.map((e) => e.card.title), midYearOnly: ran.length > 0 && ran.every((e) => e.timing.phase === "midRound") };
}

/** A card is named by its title as the card page prints it, in quotes ("the card “A Viral Post Starts a Run”"): one form on the card, Results and the memo. */
export const cardName = (title: string): string => quotedTitle(title);

/** Card names for one sentence: "A", "A and B", "A, B and C", each as the card page titles it. */
export const joinTitles = (titles: readonly string[]): string => { const names = titles.map(cardName); return names.length > 2 ? `${names.slice(0, -1).join(", ")} and ${names.at(-1)}` : names.join(" and "); };

/** "the card “A”" / "the cards “A” and “B”". */
export const cardsPhrase = (titles: readonly string[]): string => `the card${titles.length === 1 ? "" : "s"} ${joinTitles(titles)}`;

/** The net of two printed figures (their cents subtracted), so the sentence's net is the difference of the two it quotes. */
const netOf = (a: number, b: number): number => (Math.round(Number((a * 100).toPrecision(12))) - Math.round(Number((b * 100).toPrecision(12)))) / 100;

/** "$0.40 a share above the consensus you were shown" / "below", from the sign. */
const versus = (v: number): string => `${formatUsdPerShare(Math.abs(v))} a share ${v < 0 ? "below" : "above"} what the consensus you were shown implied`;

export function surpriseSentence(report: RoundReport): string | null {
  const t = report.attribution.expectationTerminal;
  if (t === undefined || Math.abs(t.surprise) < MATERIAL_USD) return null;
  const money = (v: number): string => formatUsdPerShare(v, { signed: true });
  const arrived = rateMoveCards(report).titles;
  const cards = arrived.length === 0 ? null : `from ${cardsPhrase(arrived)}`;
  const named = t.surpriseDrivers === undefined ? null : driversClause(t.surpriseDrivers, cards);
  if (named !== null) {
    const twin = t.twin === undefined ? "" : ` Your twin's value came in ${versus(t.twin.surprise)}; net ${money(netOf(t.surprise, t.twin.surprise))} a share for you against your twin.`;
    return `Your bank's value, counting the two years after the year, came in ${versus(t.surprise)}. What differed: ${named}.${twin}`;
  }
  if (t.twin === undefined) return null;
  const effect = (v: number): string => (v < 0 ? `cost ${formatUsdPerShare(Math.abs(v))}` : `added ${formatUsdPerShare(v)}`);
  return `The year differed from the consensus you were shown, for reasons the breakdown does not separate. On the value if the plan runs on, that ${effect(t.surprise)} a share to your plan and ${effect(t.twin.surprise)} a share to your twin's: net ${money(netOf(t.surprise, t.twin.surprise))} a share for you against your twin.`;
}
